import React, { useState } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, TextInput, SafeAreaView, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { rideService } from '../../services/rideService';

const isWeb = Platform.OS === 'web';

const showAlert = (title, message) => {
  if (isWeb) {
    window.alert(`${title}\n${message}`);
  } else {
    const { Alert } = require('react-native');
    Alert.alert(title, message);
  }
};

const RateRideScreen = ({ route, navigation }) => {
  const { ride } = route.params;
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      showAlert('Rating Required', 'Please select a star rating');
      return;
    }

    setSubmitting(true);
    try {
      const response = await rideService.rateRide(ride.id, rating, feedback);
      if (response.success) {
        showAlert('Thank You!', 'Your rating has been submitted.');
        // Navigate directly — don't rely on Alert callback
        navigation.navigate('MainTabs');
      } else {
        showAlert('Error', response.message || 'Failed to submit rating');
      }
    } catch (error) {
      console.error('[RateRide] Submit error:', error);
      showAlert('Error', error.response?.data?.message || 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.content}>
        {/* Fare Summary */}
        <View style={styles.fareSummary}>
          <Text style={styles.fareAmount}>
            ${Number(ride.fare || ride.estimatedFare || 0).toFixed(2)}
          </Text>
          {ride.distance > 0 && (
            <Text style={styles.fareDetail}>{Number(ride.distance).toFixed(1)} miles</Text>
          )}
          <Text style={styles.fareDetail}>
            {ride.origin} → {ride.destination}
          </Text>
          <Text style={styles.paymentLabel}>Charged to your account</Text>
        </View>

        <Text style={styles.title}>Rate Your Ride</Text>

        {ride.driver && (
          <View style={styles.driverInfo}>
            <View style={styles.driverAvatar}>
              <Ionicons name="person" size={32} color="#666" />
            </View>
            <Text style={styles.driverName}>{ride.driver.name}</Text>
            <Text style={styles.driverCar}>
              {ride.driver.vehicleModel} {ride.driver.vehiclePlate ? `• ${ride.driver.vehiclePlate}` : ''}
            </Text>
          </View>
        )}

        <View style={styles.starsContainer}>
          {[1, 2, 3, 4, 5].map((star) => (
            <TouchableOpacity key={star} onPress={() => setRating(star)} style={styles.starButton}>
              <Ionicons
                name={star <= rating ? 'star' : 'star-outline'}
                size={48}
                color={star <= rating ? '#FFD700' : '#CCCCCC'}
              />
            </TouchableOpacity>
          ))}
        </View>
        <Text style={styles.ratingLabel}>
          {rating === 0 ? 'Tap to rate' : ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][rating]}
        </Text>

        <TextInput
          style={styles.feedbackInput}
          placeholder="Leave a comment (optional)"
          placeholderTextColor="#999"
          value={feedback}
          onChangeText={setFeedback}
          multiline
          maxLength={500}
        />

        <TouchableOpacity
          style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
          onPress={handleSubmit}
          disabled={submitting}
        >
          <Text style={styles.submitButtonText}>{submitting ? 'Submitting...' : 'Submit Rating'}</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.skipButton} onPress={() => navigation.navigate('MainTabs')}>
          <Text style={styles.skipButtonText}>Skip</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#ffffff' },
  content: { flex: 1, padding: 30, alignItems: 'center', justifyContent: 'center' },
  fareSummary: { alignItems: 'center', marginBottom: 24, paddingVertical: 16, paddingHorizontal: 20, backgroundColor: '#F8F8F8', borderRadius: 12, width: '100%' },
  fareAmount: { fontSize: 36, fontWeight: 'bold', color: '#333' },
  fareDetail: { fontSize: 13, color: '#666', marginTop: 4, textAlign: 'center' },
  paymentLabel: { fontSize: 13, color: '#4CAF50', marginTop: 6, fontWeight: '600' },
  title: { fontSize: 28, fontWeight: 'bold', color: '#333', marginBottom: 8 },
  driverInfo: { alignItems: 'center', marginBottom: 30 },
  driverAvatar: {
    width: 70, height: 70, borderRadius: 35, backgroundColor: '#F0F0F0',
    justifyContent: 'center', alignItems: 'center', marginBottom: 10,
  },
  driverName: { fontSize: 18, fontWeight: 'bold', color: '#333' },
  driverCar: { fontSize: 14, color: '#666' },
  starsContainer: { flexDirection: 'row', marginBottom: 10 },
  starButton: { paddingHorizontal: 5 },
  ratingLabel: { fontSize: 16, color: '#666', marginBottom: 30 },
  feedbackInput: {
    width: '100%', borderWidth: 1, borderColor: '#E0E0E0', borderRadius: 12,
    padding: 15, fontSize: 16, minHeight: 100, textAlignVertical: 'top', marginBottom: 20,
  },
  submitButton: {
    width: '100%', backgroundColor: '#000000', borderRadius: 12,
    paddingVertical: 16, alignItems: 'center', marginBottom: 15,
  },
  submitButtonDisabled: { opacity: 0.6 },
  submitButtonText: { color: '#ffffff', fontSize: 18, fontWeight: 'bold' },
  skipButton: { paddingVertical: 10 },
  skipButtonText: { color: '#666', fontSize: 16 },
});

export default RateRideScreen;
